import { useCallback, useEffect, useRef, useState } from 'react';
import SweetAlert from '../../../components/Shared/Notification';
import { checkPhoneOptedOut } from '../../whatsapp-templates/services/optoutService';
import { interveneService } from '../services/interveneService';
import {
  buildHeaderParametersFromMedia,
  buildHeaderParametersFromUpload,
  buildTemplateSendPayload,
  buildOptimisticTemplateMessage,
  extractTemplateMessageFromSendResponse,
  getHeaderComponent,
  initBodyParams,
  initButtonUrlParams,
  isWhatsappTemplate,
  parseTemplateSendError,
  validateHeaderFile,
  validateWhatsappTemplateForm,
  getButtonsWithExamples,
} from '../utils/whatsappTemplateUtils';

const DEFAULT_LIST_FILTERS = {
  page: 0,
  search: '',
  sortField: 'created_at',
  sortOrder: 'desc',
  category: '',
  limit: 10,
  status: 'APPROVED',
};

export default function useInterveneSendTemplate({
  open,
  phoneNumber,
  botNumber,
  channelObj,
  organisationId,
  onClose,
  onTemplateSent,
}) {
  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bodyParams, setBodyParams] = useState([]);
  const [buttonUrlParams, setButtonUrlParams] = useState([]);
  const [headerFile, setHeaderFile] = useState(null);
  const [useCreateImage, setUseCreateImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);

  const templatePageRef = useRef(0);
  const templateSearchRef = useRef('');
  const loadingRef = useRef(false);

  const headerComponent = getHeaderComponent(selectedTemplate);

  const resetFormState = useCallback(() => {
    setSelectedTemplate(null);
    setBodyParams([]);
    setButtonUrlParams([]);
    setHeaderFile(null);
    setUseCreateImage(false);
    setErrors({});
  }, []);

  const getTemplateItems = useCallback((res) => {
    return res?.data?.list || res?.data?.results || [];
  }, []);

  const fetchTemplates = useCallback(
    async ({
      page = 0,
      search = '',
      append = false,
    } = {}) => {
      if (!botNumber || loadingRef.current) return;

      loadingRef.current = true;

      if (append) {
        setIsLoadingMoreTemplates(true);
      } else {
        setIsLoadingTemplates(true);
      }

      try {
        const filters = {
          ...DEFAULT_LIST_FILTERS,
          page,
          search,
          botNumber,
          companyId: channelObj?.company_id || organisationId || '',
        };

        const res = await interveneService.getWhatsappTemplateList(filters);

        if (res?.status !== 200) {
          throw new Error('Failed to fetch templates');
        }

        const items = getTemplateItems(res);

        if (append) {
          setTemplateList((prev) => {
            const existingIds = new Set(
              prev.map((item) => item?.md_id).filter(Boolean)
            );

            const newItems = items.filter(
              (item) =>
                item?.md_id &&
                !existingIds.has(item.md_id)
            );

            return [...prev, ...newItems];
          });
        } else {
          setTemplateList(items);
        }

        templatePageRef.current = page;
        templateSearchRef.current = search;

        setHasMoreTemplates(items.length >= DEFAULT_LIST_FILTERS.limit);
      } catch {
        SweetAlert('Failed to fetch templates', 'error');
      } finally {
        loadingRef.current = false;
        setIsLoadingTemplates(false);
        setIsLoadingMoreTemplates(false);
      }
    },
    [
      botNumber,
      channelObj?.company_id,
      organisationId,
      getTemplateItems,
    ]
  );

  const loadInitialTemplates = useCallback(() => {
    templatePageRef.current = 0;
    templateSearchRef.current = '';
    setHasMoreTemplates(true);

    fetchTemplates({
      page: 0,
      search: '',
      append: false,
    });
  }, [fetchTemplates]);

  const loadMoreTemplates = useCallback(() => {
    if (
      !botNumber ||
      loadingRef.current ||
      !hasMoreTemplates
    ) {
      return;
    }

    const nextPage = templatePageRef.current + 1;

    fetchTemplates({
      page: nextPage,
      search: templateSearchRef.current,
      append: true,
    });
  }, [
    botNumber,
    fetchTemplates,
    hasMoreTemplates,
  ]);

  const searchTemplates = useCallback(
    (search) => {
      const value = String(search || '').trim();

      templatePageRef.current = 0;
      templateSearchRef.current = value;
      setHasMoreTemplates(true);

      fetchTemplates({
        page: 0,
        search: value,
        append: false,
      });
    },
    [fetchTemplates]
  );

  useEffect(() => {
    if (!open) {
      resetFormState();
      setTemplateList([]);
      setHasMoreTemplates(true);
      templatePageRef.current = 0;
      templateSearchRef.current = '';
      loadingRef.current = false;
    }
  }, [open, resetFormState]);

  useEffect(() => {
    if (open && botNumber) {
      loadInitialTemplates();
    }
  }, [open, botNumber, loadInitialTemplates]);

  const selectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setHeaderFile(null);
    setUseCreateImage(false);
    setErrors({});

    if (template && isWhatsappTemplate(template)) {
      setBodyParams(initBodyParams(template));
      setButtonUrlParams(initButtonUrlParams(template));
    } else {
      setBodyParams([]);
      setButtonUrlParams([]);
    }
  }, []);

  const updateBodyParam = useCallback((index, value) => {
    setBodyParams((prev) => {
      const next = [...prev];
      const current = next[index] || {};

      next[index] = {
        type: 'text',
        text: value,
      };

      if (current.label) {
        next[index].label = current.label;
      }

      return next;
    });

    setErrors((prev) => ({
      ...prev,
      [`bodyParams_${index}`]: '',
    }));
  }, []);

  const updateButtonParam = useCallback((index, value) => {
    setButtonUrlParams((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const selectHeaderFile = useCallback((event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validation = validateHeaderFile(file);

    if (!validation.valid) {
      if (event?.target) {
        event.target.value = '';
      }

      setHeaderFile(null);

      setErrors((prev) => ({
        ...prev,
        headerFile: validation.message,
      }));

      SweetAlert(validation.message, 'error');
      return;
    }

    setHeaderFile(file);

    setErrors((prev) => ({
      ...prev,
      headerFile: '',
    }));
  }, []);

  const removeHeaderFile = useCallback(() => {
    setHeaderFile(null);

    setErrors((prev) => ({
      ...prev,
      headerFile: '',
    }));
  }, []);

  const setHeaderFileState = useCallback((file) => {
    if (!file) {
      setHeaderFile(null);

      setErrors((prev) => ({
        ...prev,
        headerFile: '',
      }));

      return;
    }

    const validation = validateHeaderFile(file);

    if (!validation.valid) {
      setHeaderFile(null);

      setErrors((prev) => ({
        ...prev,
        headerFile: validation.message,
      }));

      SweetAlert(validation.message, 'error');
      return;
    }

    setHeaderFile(file);

    setErrors((prev) => ({
      ...prev,
      headerFile: '',
    }));
  }, []);

  const toggleUseCreateImage = useCallback((checked) => {
    setUseCreateImage(checked);

    if (checked) {
      setHeaderFile(null);
    }

    setErrors((prev) => ({
      ...prev,
      headerFile: '',
    }));
  }, []);

  const resolveHeaderParameters = useCallback(async () => {
    if (!headerComponent) return null;

    if (headerComponent.format === 'TEXT') {
      return [
        {
          type: 'text',
          text: 'header text',
        },
      ];
    }

    if (useCreateImage) {
      const params = buildHeaderParametersFromMedia(
        headerComponent,
        selectedTemplate
      );

      if (!params) {
        throw new Error(
          'No media selected for header. Please upload or choose a file.'
        );
      }

      return params;
    }

    if (headerFile) {
      const headerValidation =
        validateHeaderFile(headerFile);

      if (!headerValidation.valid) {
        throw new Error(headerValidation.message);
      }

      const formData = new FormData();
      formData.append('file', headerFile);

      const uploadResponse =
        await interveneService.uploadTemplateHeaderFile(
          formData,
          botNumber
        );

      if (
        uploadResponse?.status !== 200 ||
        uploadResponse?.data?.status === false
      ) {
        throw new Error(
          parseTemplateSendError(uploadResponse)
        );
      }

      return buildHeaderParametersFromUpload(
        headerComponent,
        uploadResponse
      );
    }

    if (headerComponent.format !== 'TEXT') {
      throw new Error('Header file is required');
    }

    return null;
  }, [
    botNumber,
    headerComponent,
    headerFile,
    selectedTemplate,
    useCreateImage,
  ]);

  const submit = useCallback(async () => {
    if (
      !selectedTemplate ||
      !isWhatsappTemplate(selectedTemplate)
    ) {
      SweetAlert(
        'Please select a WhatsApp template',
        'error'
      );
      return;
    }

    if (await checkPhoneOptedOut(phoneNumber)) {
      SweetAlert(
        'Cannot send template: contact has opted out of WhatsApp messages',
        'warning'
      );
      return;
    }

    const buttonsWithExamples =
      getButtonsWithExamples(selectedTemplate);

    const nextErrors =
      validateWhatsappTemplateForm({
        phoneNumber: phoneNumber || '',
        bodyParams,
        buttonUrlParams,
        buttonsWithExamples,
        headerComponent,
        headerFile,
        useCreateImage,
      });

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const headerParameters =
        await resolveHeaderParameters();

      const payload = buildTemplateSendPayload({
        phoneNumber,
        template: selectedTemplate,
        bodyParams,
        buttonUrlParams,
        headerParameters,
      });

      const effectiveBotNumber =
        botNumber || selectedTemplate?.bot_number;

      const res =
        await interveneService.sendWhatsappTemplate(
          payload,
          effectiveBotNumber
        );

      if (res?.status === 200) {
        const serverMessage =
          extractTemplateMessageFromSendResponse(res);

        const chatMessage =
          serverMessage ||
          buildOptimisticTemplateMessage({
            phoneNumber,
            botNumber: effectiveBotNumber,
            template: selectedTemplate,
            bodyParams,
            headerParameters,
          });

        onTemplateSent?.(chatMessage);

        SweetAlert(
          'Request Processed',
          'success'
        );

        onClose?.();
      } else {
        SweetAlert(
          parseTemplateSendError(res),
          'error'
        );
      }
    } catch (err) {
      SweetAlert(
        parseTemplateSendError(
          err?.response || err
        ) ||
          err.message ||
          'Failed to send template due to a system error',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    bodyParams,
    buttonUrlParams,
    botNumber,
    headerComponent,
    headerFile,
    onClose,
    phoneNumber,
    resolveHeaderParameters,
    selectedTemplate,
    useCreateImage,
    onTemplateSent,
  ]);

  return {
    phoneNumber: phoneNumber || '',
    templateList,
    selectedTemplate,
    isLoadingTemplates,
    isLoadingMoreTemplates,
    hasMoreTemplates,
    isSubmitting,
    bodyParams,
    buttonUrlParams,
    headerComponent,
    headerFile,
    useCreateImage,
    errors,
    isWhatsappTemplate:
      isWhatsappTemplate(selectedTemplate),
    searchTemplates,
    loadMoreTemplates,
    selectTemplate,
    updateBodyParam,
    updateButtonParam,
    selectHeaderFile,
    removeHeaderFile,
    setHeaderFile: setHeaderFileState,
    toggleUseCreateImage,
    submit,
  };
}